import { useNavigate, useParams } from 'react-router-dom'

import { AvatarVariant, ResourceHeader } from '@factorialco/factorial-one-react/dist/experimental'
import { Pencil } from '@factorialco/factorial-one-react/icons/app'
import {
  TrainingClassDetailIndexPageFragment,
  TrainingsSessionInstructorDevFeatureFlagDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

function getStringFromHtml(htmlString: string): string {
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlString

  // Replace <br> and <p> with newlines before extracting text
  tempDiv.querySelectorAll('br').forEach((br) => br.replaceWith('\n'))
  tempDiv.querySelectorAll('p').forEach((p) => {
    const newline = document.createTextNode('\n')
    p.appendChild(newline)
  })

  return tempDiv.textContent || tempDiv.innerText || ''
}

const ClassDetailHeader = ({
  trainingClassInfo,
}: {
  trainingClassInfo: TrainingClassDetailIndexPageFragment
}) => {
  const { id } = useParams()

  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()

  const avatarsList: AvatarVariant[] | [] = trainingClassInfo.classMemberships.length
    ? trainingClassInfo.classMemberships.map(({ access }) => ({
        firstName: access?.firstName ?? '',
        lastName: access?.lastName ?? '',
        type: 'person',
        src: buildUrl(access?.avatar ?? ''),
      }))
    : []

  const description = trainingClassInfo?.description
    ? getStringFromHtml(trainingClassInfo?.description)
    : ''

  const { data: featuresData, loading: loadingFeatures } = useQuery(
    TrainingsSessionInstructorDevFeatureFlagDocument,
    {
      variables: {
        companyId: company.id,
      },
    }
  )
  const devTrainingsSessionInstructorFF = featuresData?.features.featuresConnection.nodes.find(
    (n) => n.name === 'dev_trainings_session_instructor' && n.enabled == true
  )

  const getUniqueSessionsInstructors = (): AvatarVariant[] => {
    const sessionInstructors = trainingClassInfo.sessions.flatMap(
      (session) => session.sessionInstructors || []
    )
    const instructorsSet = new Set()
    const instructorsUniqueIds = sessionInstructors.length
      ? sessionInstructors.filter(
          (item) => !instructorsSet.has(item.employee.id) && instructorsSet.add(item.employee.id)
        )
      : []
    const instructorsList: AvatarVariant[] = instructorsUniqueIds.length
      ? instructorsUniqueIds?.map((instructor) => ({
          firstName: instructor.employee.firstName,
          lastName: instructor.employee.lastName,
          type: 'person' as const,
          src: buildUrl(instructor.employee?.avatar?.url ?? ''),
        }))
      : []
    return instructorsList
  }
  return (
    <ResourceHeader
      title={trainingClassInfo.name}
      description={description}
      metadata={[
        {
          label: i18n.t('trainings.trainings_revamp.classes.header.startDate'),
          value: {
            type: 'text',
            content: dateToString(moment(trainingClassInfo.startDate), 'ui_dmy_long'),
          },
        },
        {
          label: i18n.t('trainings.trainings_revamp.classes.header.endDate'),
          value: {
            type: 'text',
            content: dateToString(moment(trainingClassInfo.endDate), 'ui_dmy_long'),
          },
        },
        {
          label: i18n.t('trainings.trainings_revamp.classes.header.participants'),
          value: {
            type: 'list',
            variant: 'person',
            avatars: avatarsList,
          },
        },
        ...(!loadingFeatures && devTrainingsSessionInstructorFF
          ? [
              {
                label: i18n.t('trainings.trainings_revamp.classes.header.instructors'),
                value: {
                  type: 'list' as const,
                  variant: 'person' as const,
                  avatars: getUniqueSessionsInstructors(),
                },
              },
            ]
          : []),
      ]}
      secondaryActions={[
        {
          label: i18n.t('trainings.trainings_revamp.classes.header.delete'),
          onClick: () =>
            navigate(
              navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.deleteClass.fullPath(
                {
                  id: Number(id),
                  classId: Number(trainingClassInfo.id),
                }
              )
            ),
        },
        {
          label: i18n.t('trainings.trainings_revamp.classes.header.edit'),
          icon: Pencil,
          onClick: () =>
            navigate(
              navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.editClass.fullPath(
                {
                  id: Number(id),
                  classId: trainingClassInfo.id,
                }
              )
            ),
        },
      ].filter(Boolean)}
    />
  )
}
export default ClassDetailHeader
